import { BufferGeometry, Line, Line3, LineBasicMaterial, Vector3 } from 'three';
import { watchImmediate } from '@vueuse/core';
import { stop, stopAll, type StopHandle } from '@/util';
import type { OverlaySceneContext } from '@/views/dicoms/overlay/scene/overlaySceneContext';
import type { OverlayStore } from '@/views/dicoms/overlay/overlayStore';
import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
import type { CatstacksData } from "@/api/catstacksData";
import { reactive } from "vue";
import { indicatorBarColour } from "@/views/dicoms/CatStackImageUtil";

type IndicatorLineType = 'horizontal' | 'vertical' | 'none';

interface ViewDimensions {
    width: number;
    height: number;
    halfWidth: number;
    halfHeight: number;
}

interface LinePositioning {
    lineBasePosition: Vector3;
    rightVector: Vector3;
    upVector: Vector3;
}

/** Update indicator line for a specific view type */
export function updateIndicatorLine(
    context: OverlaySceneContext,
    store: OverlayStore,
    planeId: CatstackPlaneId,
): StopHandle {
    let lineUpdate: StopHandle | null = null;

    return stopAll(
        watchImmediate(
            () => ({
                selectedView: store.catstackMode,
                catstacksData: store.catstacksData,
            }),
            ({ selectedView, catstacksData }) => {
                stop(lineUpdate);
                lineUpdate = null;

                // Show indicator lines only on non-selected views
                if (catstacksData && selectedView !== planeId) {
                    lineUpdate = _updateIndicatorLineGeometry(
                        context,
                        store,
                        planeId,
                        selectedView,
                        catstacksData,
                    );
                }
            },
        ),
        () => stop(lineUpdate),
    );
}

function _updateIndicatorLineGeometry(
    context: OverlaySceneContext,
    store: OverlayStore,
    viewType: CatstackPlaneId,
    selectedView: CatstackPlaneId,
    catstacksData: CatstacksData,
): StopHandle {
    const indicatorLineType = _getIndicatorLineType(selectedView, viewType);

    if (indicatorLineType === 'none' || !catstacksData) {
        return () => {
        };
    }

    const indicatorLine = reactive<Line3>(new Line3());
    let line: Line | null = null;

    return stopAll(
        watchImmediate(
            () => ({
                selectedViewSliceIndex: store.catstacks[selectedView]?.sliceIndex,
                catstacksData: store.catstacksData,
            }),
            ({ selectedViewSliceIndex, catstacksData }) => {
                _cleanupExistingLine(context, line)
                line = null;

                if (selectedViewSliceIndex !== undefined && catstacksData) {
                    const linePoints = _calculateIndicatorLinePoints(
                        viewType,
                        indicatorLineType,
                        selectedView,
                        selectedViewSliceIndex,
                        catstacksData,
                    );

                    if (linePoints && linePoints.length === 2) {
                        // Update the reactive Line3
                        indicatorLine.start.copy(linePoints[0]);
                        indicatorLine.end.copy(linePoints[1]);

                        // Create the visual line geometry
                        const geometry = new BufferGeometry().setFromPoints(linePoints);
                        const material = new LineBasicMaterial({
                            color: indicatorBarColour,
                            transparent: false,
                            depthTest: false, // Render on top
                        });

                        line = new Line(geometry, material);
                        context.scene.add(line);
                    }
                }
            },
        ),
        () => _cleanupExistingLine(context, line)
    );
}

function _cleanupExistingLine(context: OverlaySceneContext, line: Line | null): void {
    if (line) {
        context.scene.remove(line);
        line.geometry.dispose();
        (line.material as LineBasicMaterial).dispose();
    }
}

function _getIndicatorLineType(planeId: CatstackPlaneId, currentView: CatstackPlaneId): IndicatorLineType {
    switch (planeId) {
        case "axial":
            return "horizontal";
        case "coronal":
            return currentView === 'axial' ? 'horizontal' : 'vertical';
        case "sagittal":
            return "vertical";
        default:
            return "none"
    }
}

function _calculateIndicatorLinePoints(
    planeId: CatstackPlaneId,
    indicatorLineType: IndicatorLineType,
    selectedPlaneId: CatstackPlaneId,
    selectedViewSliceIndex: number,
    catstacksData: CatstacksData,
): Vector3[] | null {

    const currentPlaneData = catstacksData[planeId];
    if (!currentPlaneData) {
        console.log(`[IndicatorLine] No data for ct plane: ${ planeId }`);
        return null;
    }

    const indicatorWorldPosition = _getIndicatorWorldPosition(
        selectedPlaneId,
        selectedViewSliceIndex,
        catstacksData
    );

    if (!indicatorWorldPosition) return null;

    const dimensions = _getViewDimensions(currentPlaneData);
    const positioning = _calculateLinePositioning(planeId, currentPlaneData);
    const positionRelativeToOrigin = indicatorWorldPosition.clone().sub(currentPlaneData.axis.origin);

    return _generateLinePoints(indicatorLineType, dimensions, positioning, positionRelativeToOrigin);
}

function _getViewDimensions(currentViewData: any): ViewDimensions {
    const width = currentViewData.catstack.world.width;
    const height = currentViewData.catstack.world.height;

    return {
        width,
        height,
        halfWidth: width / 2,
        halfHeight: height / 2,
    };
}

function _calculateLinePositioning(planeId: CatstackPlaneId, currentViewData: any): LinePositioning {
    const maxSliceIndex = currentViewData.catstack.count - 1;
    const sliceSpacing = currentViewData.axis.length / (currentViewData.catstack.count - 1);
    const maxSliceOffset = sliceSpacing * maxSliceIndex;
    const lineDepthOffset = maxSliceOffset + (sliceSpacing * 0.1);

    const lineBasePosition = currentViewData.axis.direction.clone()
        .multiplyScalar(lineDepthOffset)
        .add(currentViewData.axis.origin);

    const vectors = _getViewVectors(planeId);

    return {
        lineBasePosition,
        rightVector: vectors.right,
        upVector: vectors.up,
    };
}

function _getViewVectors(planeId: CatstackPlaneId): { right: Vector3; up: Vector3 } {
    switch (planeId) {
        case 'axial':
            return {
                right: new Vector3(1, 0, 0),
                up: new Vector3(0, 1, 0),
            };
        case 'coronal':
            return {
                right: new Vector3(1, 0, 0),
                up: new Vector3(0, 0, 1),
            };
        case 'sagittal':
            return {
                right: new Vector3(0, 1, 0),
                up: new Vector3(0, 0, 1),
            };
        default:
            return {
                right: new Vector3(1, 0, 0),
                up: new Vector3(0, 1, 0),
            };
    }
}

function _generateLinePoints(
    indicatorLineType: IndicatorLineType,
    dimensions: ViewDimensions,
    positioning: LinePositioning,
    positionRelativeToOrigin: Vector3,
): Vector3[] | null {
    const { halfWidth, halfHeight } = dimensions;
    const { lineBasePosition, rightVector, upVector } = positioning;

    switch (indicatorLineType) {
        case 'horizontal':
            return _generateHorizontalLinePoints(
                lineBasePosition,
                rightVector,
                upVector,
                positionRelativeToOrigin,
                halfWidth
            );
        case "vertical":
            return _generateVerticalLinePoints(
                lineBasePosition,
                rightVector,
                upVector,
                positionRelativeToOrigin,
                halfHeight
            );
        default:
            return null;
    }
}

function _generateHorizontalLinePoints(
    lineBasePosition: Vector3,
    rightVector: Vector3,
    upVector: Vector3,
    positionRelativeToOrigin: Vector3,
    halfWidth: number,
): Vector3[] {
    const upOffset = positionRelativeToOrigin.dot(upVector);
    const offsetVector = upVector.clone().multiplyScalar(upOffset);
    const lineCenter = lineBasePosition.clone().add(offsetVector);

    return [
        lineCenter.clone().add(rightVector.clone().multiplyScalar(-halfWidth)),
        lineCenter.clone().add(rightVector.clone().multiplyScalar(halfWidth))
    ];
}

function _generateVerticalLinePoints(
    lineBasePosition: Vector3,
    rightVector: Vector3,
    upVector: Vector3,
    positionRelativeToOrigin: Vector3,
    halfHeight: number,
): Vector3[] {
    const rightOffset = positionRelativeToOrigin.dot(rightVector);
    const offsetVector = rightVector.clone().multiplyScalar(rightOffset);
    const lineCenter = lineBasePosition.clone().add(offsetVector);

    return [
        lineCenter.clone().add(upVector.clone().multiplyScalar(-halfHeight)),
        lineCenter.clone().add(upVector.clone().multiplyScalar(halfHeight))
    ];
}

function _getIndicatorWorldPosition(
    selectedPlaneId: CatstackPlaneId,
    selectedViewSliceIndex: number,
    catstacksData: CatstacksData,
): Vector3 | null {
    const selectedViewData = catstacksData[selectedPlaneId];
    if (!selectedViewData) return null;

    const sliceSpacing = selectedViewData.axis.length / (selectedViewData.catstack.count - 1);
    const sliceOffset = sliceSpacing * selectedViewSliceIndex;

    return selectedViewData.axis.direction.clone()
        .multiplyScalar(sliceOffset)
        .add(selectedViewData.axis.origin);
}
